const nzdFormatter = new Intl.NumberFormat("en-NZ", {
  style: "currency",
  currency: "NZD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatNZD(value: number): string {
  if (!Number.isFinite(value)) return "$0.00";
  return nzdFormatter.format(value);
}

const percentFormatter = new Intl.NumberFormat("en-NZ", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export function formatPercentage(value: number): string {
  if (!Number.isFinite(value)) return "0.0%";
  return percentFormatter.format(value / 100);
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return "-";

  const totalSeconds = Math.round(seconds);

  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

const numberFormatter = new Intl.NumberFormat("en-NZ");

export function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return numberFormatter.format(value);
}

const dateTimeFormatter = new Intl.DateTimeFormat("en-NZ", {
  dateStyle: "medium",
  timeStyle: "short",
});

export function formatDateTime(isoString: string | null): string {
  if (!isoString) return "-";
  try {
    return dateTimeFormatter.format(new Date(isoString));
  } catch {
    return "-";
  }
}

const shortDateFormatter = new Intl.DateTimeFormat("en-NZ", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/**
 * Format a pay period date range, e.g. "30 Dec 2024 \u2013 12 Jan 2025".
 *
 * Appends `T00:00:00` (no `Z`) so date-only strings like "2025-01-14" are
 * parsed as **local** midnight rather than UTC midnight. Without this,
 * `Intl.DateTimeFormat` in NZ (UTC+12/+13) rolls the displayed date back
 * to the previous day.
 */
export function formatPayPeriod(start: string, end: string): string {
  try {
    const s = shortDateFormatter.format(new Date(start + "T00:00:00"));
    const e = shortDateFormatter.format(new Date(end + "T00:00:00"));
    return `${s} \u2013 ${e}`;
  } catch {
    return `${start} \u2013 ${end}`;
  }
}
